import React, { useState } from 'react';
import { borrowingApi } from '../services/api';
import '../styles/Modal.css';

function UploadBorrowingModal({ entity, onClose, onComplete }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx')) {
        setError('Please select an Excel file (.xlsx)');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (!file.name.endsWith('.xlsx')) {
        setError('Please select an Excel file (.xlsx)');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const data = await borrowingApi.uploadBorrowingData(entity.name, selectedFile);
      setResult(data);
      if (onComplete) {
        setTimeout(() => onComplete(), 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to upload borrowing data');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Borrowing & Finance Costs Data</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {result && (
            <div className="alert alert-success">
              Successfully uploaded borrowing data!
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li>Total Records: {result.summary.borrowing_count}</li>
                <li>Loan Types: {result.summary.loan_types_count}</li>
                <li>Number of Lenders/Banks: {result.summary.lender_count}</li>
                {result.summary.loan_types && Object.keys(result.summary.loan_types).length > 0 && (
                  <li style={{ marginTop: '8px' }}>
                    <strong>Breakdown by Type:</strong>
                    <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                      {Object.entries(result.summary.loan_types).map(([type, count]) => (
                        <li key={type}>
                          {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {count} record(s)
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
              </ul>
            </div>
          )}

          {!result && (
            <>
              <p>Upload Borrowing & Finance Costs input data for {entity.uiName}</p>

              {/* File Upload Area */}
              <div
                className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="borrowing-file-input"
                  accept=".xlsx"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <label htmlFor="borrowing-file-input" className="file-upload-label">
                  {selectedFile ? (
                    <>
                      <div className="file-icon">📄</div>
                      <div className="file-name">{selectedFile.name}</div>
                      <div className="file-size">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="file-icon">📁</div>
                      <div>Click to browse or drag and drop</div>
                      <div className="file-hint">Excel files only (.xlsx)</div>
                    </>
                  )}
                </label>
              </div>

              {selectedFile && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedFile(null)}
                  style={{ marginTop: '10px' }}
                >
                  Clear Selection
                </button>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {!result && (
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UploadBorrowingModal;
